import styled from '@emotion/styled';
import type { ReactNode } from 'react';

import { Container } from '@/components/ui/Container';
import { colors, mq, mqMax, spacing } from '@/lib/theme';

import { PageFooter } from './PageFooter';

const Main = styled.div<{
	bottomSpace?: boolean;
	white?: boolean;
	whiteOnPhones?: boolean;
}>`
	min-height: 100dvh;
	display: flex;
	flex-direction: column;

	${({ white }) => white && `background: ${colors.WH100};`}

	${({ whiteOnPhones }) =>
		whiteOnPhones &&
		`
		${mqMax.md} {
			background: ${colors.WH100};
		}
	`}

	/* Room below the footer for a fixed bottom bar (phones only). */
	${({ bottomSpace }) =>
		bottomSpace &&
		`
		padding-bottom: calc(104px + env(safe-area-inset-bottom));
		${mq.lg} {
			padding-bottom: 0;
		}
	`}
`;

const Content = styled.main`
	flex: 1;
	padding-bottom: ${spacing.space60};
`;

interface PageShellProps {
	/** The `SiteHeader` for this page, if it has one. */
	header?: ReactNode;
	/** Single-column flows read better at a narrower width. */
	narrow?: boolean;
	/** Leave room above the footer for a fixed bottom bar on phones. */
	bottomSpace?: boolean;
	/** A white page at every size, for pages laid out without panels. */
	white?: boolean;
	/** The page is one white sheet on phones, so the footer sits on white too. */
	whiteOnPhones?: boolean;
	children: ReactNode;
}

/**
 * The frame around every page: header, content and footer in one centred
 * column. Pages opt in through `getLayout` (see `_app.tsx`).
 */
export const PageShell = ({
	header,
	narrow,
	bottomSpace,
	white,
	whiteOnPhones,
	children,
}: PageShellProps) => (
	<Main bottomSpace={bottomSpace} white={white} whiteOnPhones={whiteOnPhones}>
		<Container narrow={narrow}>
			{header}
			<Content>{children}</Content>
		</Container>
		<PageFooter narrow={narrow} />
	</Main>
);
